/**
 * Shrinks a photo in the browser before it is uploaded.
 *
 * A phone camera produces 4-5MB per shot. Five of those per listing would fill
 * the free database tier after about twenty listings, and would be miserable to
 * load on Kenyan mobile data — the people this site is for are browsing on a
 * phone, often on a bundle.
 *
 * Doing it here rather than on the server is deliberate: resizing server-side
 * needs a native image library, which is an awkward dependency on a free
 * container, and it would still have paid the cost of uploading the original
 * over the landlord's own connection. The canvas is already on the device, the
 * photo is already in memory, and the upload that follows is a tenth the size.
 *
 * The server keeps its own cap regardless — see MAX_PHOTO_BYTES. This makes the
 * common case pleasant; that stops the uncommon one.
 */

/** Longest edge, in pixels. Comfortably above what the gallery renders. */
const MAX_EDGE = 1600;

/** JPEG quality. 0.82 is the point where artefacts stop being visible on a
 *  photograph but the file is a fraction of the original. */
const QUALITY = 0.82;

export interface Downscaled {
  /** Base64 WITHOUT the data: prefix, which is what the API expects. */
  contentBase64: string;
  mimeType: 'image/jpeg' | 'image/png' | 'image/webp';
  bytes: number;
  width: number;
  height: number;
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    // Revoked on both paths: an object URL held after a failed decode leaks
    // the whole file for the life of the page.
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('That image could not be read.')); };
    img.src = url;
  });
}

export async function downscale(file: File): Promise<Downscaled> {
  const img = await loadImage(file);

  const scale = Math.min(1, MAX_EDGE / Math.max(img.width, img.height));
  const width = Math.max(1, Math.round(img.width * scale));
  const height = Math.max(1, Math.round(img.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width; canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('That image could not be processed on this device.');
  ctx.drawImage(img, 0, 0, width, height);

  // PNG is kept for images that are actually PNG-shaped (screenshots, plans):
  // re-encoding line art as JPEG makes it look worse AND bigger. Everything
  // else becomes JPEG, which is what a photograph should be.
  const keepPng = file.type === 'image/png' && file.size <= 1_000_000;
  const mimeType = keepPng ? 'image/png' as const : 'image/jpeg' as const;

  const dataUrl = canvas.toDataURL(mimeType, QUALITY);
  const contentBase64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
  // Base64 carries 3 bytes in every 4 characters, less the padding.
  const bytes = Math.floor(contentBase64.length * 3 / 4)
    - (contentBase64.endsWith('==') ? 2 : contentBase64.endsWith('=') ? 1 : 0);

  return { contentBase64, mimeType, bytes, width, height };
}

/** Human-readable size, for the "1.2MB → 180KB" line beside a thumbnail. */
export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/* -------------------------------------------------------------------------- */
/* Two sizes in one pass                                                      */
/* -------------------------------------------------------------------------- */

/** Longest edge for the grid thumbnail. Twice the rendered size, so it stays
 *  sharp on a phone's 2x screen and no larger. */
const THUMB_EDGE = 320;
const THUMB_QUALITY = 0.7;

export interface DownscaledPair extends Downscaled {
  /** Base64 JPEG thumbnail, without the data: prefix. */
  thumbBase64: string;
  thumbBytes: number;
}

/**
 * Produces the upload AND its thumbnail from one decode.
 *
 * An inspection is the case that makes this worth doing: a move-out can carry
 * forty photographs, and a checklist that fetched forty full-size images to
 * draw forty 150px squares would be unusable on the connection the person is
 * standing on. The thumbnail is made here, on the device that already has the
 * pixels in memory, and stored beside the original — so the grid is a few
 * hundred kilobytes and the original is fetched only when somebody taps.
 *
 * Decoding once and drawing twice, rather than calling downscale() twice: the
 * decode is the expensive half, and a phone doing it twice per photo is
 * noticeable while walking a property.
 */
export async function downscaleWithThumb(file: File): Promise<DownscaledPair> {
  const img = await loadImage(file);

  const render = (maxEdge: number, mime: string, quality: number) => {
    const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
    const width = Math.max(1, Math.round(img.width * scale));
    const height = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width; canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('That image could not be processed on this device.');
    ctx.drawImage(img, 0, 0, width, height);
    const dataUrl = canvas.toDataURL(mime, quality);
    return { base64: dataUrl.slice(dataUrl.indexOf(',') + 1), width, height };
  };

  const keepPng = file.type === 'image/png' && file.size <= 1_000_000;
  const mimeType = keepPng ? 'image/png' as const : 'image/jpeg' as const;

  const full = render(MAX_EDGE, mimeType, QUALITY);
  // Always JPEG: a thumbnail is a photograph of a photograph, and nothing is
  // gained by keeping it lossless.
  const thumb = render(THUMB_EDGE, 'image/jpeg', THUMB_QUALITY);

  return {
    contentBase64: full.base64,
    mimeType,
    bytes: base64Bytes(full.base64),
    width: full.width,
    height: full.height,
    thumbBase64: thumb.base64,
    thumbBytes: base64Bytes(thumb.base64),
  };
}

function base64Bytes(b64: string): number {
  return Math.floor(b64.length * 3 / 4)
    - (b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0);
}

/* -------------------------------------------------------------------------- */
/* 360° panoramas                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Equirectangular width. 4096×2048 is the sweet spot: sharp enough that a
 * zoomed-in wall still reads, inside the 4096 texture limit that even modest
 * Android GPUs guarantee, and a power of two — which is what lets the viewer
 * use REPEAT wrapping and lose the seam behind you.
 */
const PANO_WIDTH = 4096;
const PANO_QUALITY = 0.85;

export class NotEquirectangular extends Error {
  constructor(readonly ratio: number) {
    super('A 360° photo must be twice as wide as it is tall. '
      + `This one is ${ratio.toFixed(2)}:1 — it looks like an ordinary photo rather than a 360 capture.`);
  }
}

/**
 * Prepares a 360 capture for upload.
 *
 * REFUSES a photo that is not 2:1, and that refusal is the useful part. Every
 * 360 camera and every phone panorama mode writes equirectangular 2:1; an
 * ordinary photo dropped into this slot would upload happily and then render
 * as a warped, unreadable mess in the viewer, with nothing to say why. Better
 * to say so here, while the landlord still has the right file to hand.
 *
 * The tolerance is loose (1.9–2.1) because some cameras crop a few pixels.
 */
export async function downscalePanorama(file: File): Promise<Downscaled> {
  const img = await loadImage(file);
  const ratio = img.width / img.height;
  if (ratio < 1.9 || ratio > 2.1) throw new NotEquirectangular(ratio);

  const width = Math.min(PANO_WIDTH, img.width);
  const height = Math.round(width / 2);

  const canvas = document.createElement('canvas');
  canvas.width = width; canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('That image could not be processed on this device.');
  ctx.drawImage(img, 0, 0, width, height);

  // Always JPEG: a panorama is a photograph, and PNG at this size would be
  // twenty megabytes for no visible gain.
  const dataUrl = canvas.toDataURL('image/jpeg', PANO_QUALITY);
  const contentBase64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
  const bytes = Math.floor(contentBase64.length * 3 / 4)
    - (contentBase64.endsWith('==') ? 2 : contentBase64.endsWith('=') ? 1 : 0);

  return { contentBase64, mimeType: 'image/jpeg', bytes, width, height };
}

/**
 * Reads a video's duration without uploading it, so the form can show it and
 * the server can store it. Resolves to undefined rather than throwing: a
 * duration is a nicety, and a browser that will not report one must not block
 * the upload.
 */
export function videoDuration(file: File): Promise<number | undefined> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const el = document.createElement('video');
    el.preload = 'metadata';
    const done = (v: number | undefined) => { URL.revokeObjectURL(url); resolve(v); };
    el.onloadedmetadata = () => done(Number.isFinite(el.duration) ? Math.round(el.duration) : undefined);
    el.onerror = () => done(undefined);
    el.src = url;
  });
}

/**
 * Length and frame size of a video, read from its metadata in the browser.
 *
 * The frame size is what tells a 360 film from an ordinary one: an
 * equirectangular frame is exactly twice as wide as it is tall (3840x1920,
 * 5760x2880), and a phone video is 16:9 or 9:16. Every field is undefined when
 * the browser cannot read the file, and the upload then goes ahead as before.
 */
export function videoShape(file: File): Promise<{ seconds?: number; width?: number; height?: number }> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const el = document.createElement('video');
    el.preload = 'metadata';
    const done = (v: { seconds?: number; width?: number; height?: number }) => {
      URL.revokeObjectURL(url); resolve(v);
    };
    el.onloadedmetadata = () => done({
      seconds: Number.isFinite(el.duration) ? Math.round(el.duration) : undefined,
      width: el.videoWidth || undefined,
      height: el.videoHeight || undefined,
    });
    el.onerror = () => done({});
    el.src = url;
  });
}

/** True for a 2:1 frame, allowing the odd pixel an encoder rounds off. */
export function isEquirectFrame(width?: number, height?: number): boolean {
  if (!width || !height) return false;
  return Math.abs(width / height - 2) < 0.02;
}
